import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosInstance";

const fmt = (v) => '₹' + Math.round(parseFloat(v) || 0).toLocaleString('en-IN');
const fmtDate = (v) => {
    if (!v) return '--';
    const s = v.toString().substring(0, 10);
    const [y, m, d] = s.split('-');
    return `${d}-${m}-${y}`;
};

const BUCKETS = [
    { label: 'Critical (60d+)',  min: 60,  max: Infinity, color: '#dc3545', badge: 'danger'  },
    { label: 'High (30–60d)',    min: 30,  max: 59,       color: '#fd7e14', badge: 'warning' },
    { label: 'Medium (15–29d)', min: 15,  max: 29,       color: '#ffc107', badge: 'warning' },
    { label: 'Low (1–14d)',     min: 1,   max: 14,       color: '#28a745', badge: 'success' },
    { label: 'No Overdue',      min: 0,   max: 0,        color: '#6c757d', badge: 'secondary' },
];

function ageBucket(loan) {
    const db  = parseFloat(loan.due_balance)  || 0;
    const fb  = parseFloat(loan.fine_balance) || 0;
    if (db === 0 && fb === 0) return 4;
    const date = loan.last_due_date || loan.date;
    if (!date) return 3;
    const daysOverdue = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
    if (daysOverdue >= 60) return 0;
    if (daysOverdue >= 30) return 1;
    if (daysOverdue >= 15) return 2;
    return 3;
}

export default function AccountsOutstandingAging() {
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab]         = useState(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.get('/finance/open');
                setRows(Array.isArray(res.data) ? res.data : []);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        })();
    }, []);

    const bucketed = BUCKETS.map((b, bi) => ({
        ...b,
        loans: rows.filter((l) => ageBucket(l) === bi),
    }));
    const activeTab = tab ?? 0;
    const shown = bucketed[activeTab]?.loans || [];

    const totalDue  = shown.reduce((s, l) => s + (parseFloat(l.due_balance)  || 0), 0);
    const totalFine = shown.reduce((s, l) => s + (parseFloat(l.fine_balance) || 0), 0);

    const exportCSV = () => {
        const headers = 'Reg No,Borrower,City,Date,Loan Amt,EMI,Due Balance,Fine Balance,Total';
        const data = shown.map(l => {
            const db = parseFloat(l.due_balance) || 0;
            const fb = parseFloat(l.fine_balance) || 0;
            return [l.reg_no, l.borrower_name, l.borrower_city, fmtDate(l.date), l.loan_amount, l.emi_amount, db || '', fb || '', db + fb].map(v => `"${v ?? ''}"`).join(',');
        });
        const blob = new Blob([[headers, ...data].join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'Outstanding-Aging.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="content-wrapper">
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0">
                                <i className="fas fa-exclamation-circle text-danger mr-2"></i>Outstanding & Aging
                            </h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/accounts">Accounts</a></li>
                                <li className="breadcrumb-item active">Outstanding & Aging</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <section className="content">
                <div className="container-fluid">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-danger" role="status"></div></div>
                    ) : (
                        <>
                            {/* Summary row */}
                            <div className="row mb-3">
                                {bucketed.map((b, bi) => (
                                    <div key={bi} className="col-md-2 col-sm-4 mb-2">
                                        <div
                                            className={`card elevation-2 text-center ${activeTab === bi ? 'border-primary' : ''}`}
                                            style={{ cursor: 'pointer', borderLeft: `4px solid ${b.color}` }}
                                            onClick={() => setTab(bi)}
                                        >
                                            <div className="card-body py-2 px-1">
                                                <p className="mb-0 small text-muted">{b.label}</p>
                                                <h4 className={`mb-0 font-weight-bold text-${b.badge}`}>{b.loans.length}</h4>
                                                <small className="text-muted">loans</small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Active bucket header */}
                            {shown.length > 0 && (
                                <div className="row mb-2 align-items-center">
                                    <div className="col-md-6">
                                        <div className="callout callout-danger py-2">
                                            <span className="mr-3">Due Balance: <strong>{fmt(totalDue)}</strong></span>
                                            <span>Fine Balance: <strong>{fmt(totalFine)}</strong></span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 text-right">
                                        <button className="btn btn-sm btn-outline-danger mr-1" onClick={exportCSV}>
                                            <i className="fas fa-file-csv mr-1"></i>Export
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Table */}
                            <div className="card">
                                <div className="card-body p-0">
                                    {shown.length === 0 ? (
                                        <div className="text-center py-5 text-muted">
                                            <i className="fas fa-check-circle fa-3x mb-3 d-block text-success"></i>
                                            No loans in this bucket
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-hover table-sm mb-0">
                                                <thead className="thead-light">
                                                    <tr><th>#</th><th>Reg No</th><th>Borrower</th><th>City</th><th>Date</th><th>Loan Amt</th><th>EMI</th><th>Due Bal</th><th>Fine Bal</th><th>Total</th></tr>
                                                </thead>
                                                <tbody>
                                                    {shown.map((l, i) => {
                                                        const db = parseFloat(l.due_balance) || 0;
                                                        const fb = parseFloat(l.fine_balance) || 0;
                                                        return (
                                                            <tr key={i}>
                                                                <td>{i + 1}</td>
                                                                <td>{l.reg_no || '--'}</td>
                                                                <td>{l.borrower_name || '--'}</td>
                                                                <td>{l.borrower_city || '--'}</td>
                                                                <td>{fmtDate(l.date)}</td>
                                                                <td>{fmt(l.loan_amount)}</td>
                                                                <td>{fmt(l.emi_amount)}</td>
                                                                <td className={db > 0 ? 'text-warning font-weight-bold' : ''}>{db > 0 ? fmt(db) : '--'}</td>
                                                                <td className={fb > 0 ? 'text-danger font-weight-bold' : ''}>{fb > 0 ? fmt(fb) : '--'}</td>
                                                                <td className="font-weight-bold">{fmt(db + fb)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
